import { useEffect, useState } from 'react'
import { Alert, Button, Card, Form, Spinner, Row, Col} from 'react-bootstrap' 
import { useMutation, useQueryClient } from 'react-query';
import { useOrgContext } from '../../context/org';
import { IAgent } from '../../models/agent';
import { updateAgent } from '../../api/agent';
import { FaEdit, FaCheck, FaExclamationCircle } from 'react-icons/fa';

type Props = {
    agent: IAgent;
    orgId: string;
}

function AgentSettings({orgId, agent}: Props) {
    const [name, setName] = useState(agent.name)
    const [description, setDescription] = useState(agent.description);
    const [error, setError] = useState<string | null>(null)
    const [message, setMessage] = useState<string | null>(null);

    const {membership} = useOrgContext();
    const isAllowed = ["owner", "admin", "editor"].includes(membership?.role || "");

    // const allowed = ["owner", "admin", "editor"]
    // const permission = !allowed.includes(membership?.role || "");

    const queryClient = useQueryClient();
    const {mutateAsync: updateAgentFn, isLoading,} = useMutation(updateAgent, {
        onSuccess: (data) => {
            queryClient.invalidateQueries(['Agents', orgId]);
            setMessage("Agent updated successfully.")
            setError(null);
        },
        onError: (error) => {
            setError("An error occurred. Please try again later.")
            setMessage(null);
        }
    })

    const handleUpdate = (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        if(!agent) {
            return;
        }

        updateAgentFn({
            id: agent.id,
            orgId,
            name,
            description,
        })
    }

    useEffect(() => {
        if (agent) {
            setName(agent.name);
            setDescription(agent.description);
        }
    }, [agent])

    return (
        <Card style={{ backgroundColor: '#2d2d2d', borderColor: '#444' }}>
            <Card.Header style={{ backgroundColor: '#2d2d2d', borderBottom: '1px solid #444' }}>
                <div className="d-flex align-items-center">
                    <div className="bg-primary rounded p-2 me-3">
                        <FaEdit className="text-white" />
                    </div>
                    <div>
                        <h5 className="mb-0 text-light">Basic Information</h5>
                        <small style={{ color: '#999' }}>Update your agent's name and description</small>
                    </div>
                </div>
            </Card.Header>
            <Card.Body>
                <Form onSubmit={handleUpdate}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold text-light">
                            Agent Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control 
                            disabled={!isAllowed}
                            type="text" 
                            value={name} 
                            placeholder='Enter agent name' 
                            onChange={e => setName(e.target.value)}
                            style={{ 
                                backgroundColor: !isAllowed ? '#444' : '#1a1a1a', 
                                borderColor: '#555',
                                color: '#fff'
                            }}
                        />
                        <Form.Text style={{ color: '#999' }}>
                            Choose a descriptive name for your agent
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold text-light">Description</Form.Label>
                        <Form.Control 
                            disabled={!isAllowed}
                            as="textarea" 
                            rows={4}
                            value={description} 
                            placeholder='Enter a brief description of your agent' 
                            onChange={e => setDescription(e.target.value)}
                            style={{ 
                                backgroundColor: !isAllowed ? '#444' : '#1a1a1a', 
                                borderColor: '#555',
                                color: '#fff'
                            }}
                        />
                        <Form.Text style={{ color: '#999' }}>
                            Briefly describe what this agent does
                        </Form.Text>
                    </Form.Group>

                    {error && (
                        <Alert 
                            variant="danger" 
                            className="d-flex align-items-center"
                            style={{ backgroundColor: '#2c1618', borderColor: '#dc3545', color: '#f5c6cb' }}
                        >
                            <FaExclamationCircle className="me-2" />
                            {error}
                        </Alert>
                    )}

                    {message && (
                        <Alert 
                            variant="success" 
                            className="d-flex align-items-center"
                            style={{ backgroundColor: '#1a2e1a', borderColor: '#28a745', color: '#d4edda' }}
                        >
                            <FaCheck className="me-2" />
                            {message}
                        </Alert>
                    )}

                    {!isAllowed && (
                        <Alert 
                            variant="warning" 
                            className="d-flex align-items-center"
                            style={{ backgroundColor: '#2d2a1a', borderColor: '#ffc107', color: '#fff3cd' }}
                        >
                            <FaExclamationCircle className="me-2" />
                            You don't have permission to edit this agent. Contact your organization admin for access.
                        </Alert>
                    )}

                    <div className="d-flex justify-content-end">
                        <Button 
                            disabled={!isAllowed || isLoading} 
                            type="submit" 
                            variant="primary"
                            className="px-4"
                        >
                            {isLoading ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <FaCheck className="me-2" />
                                    Update Agent
                                </>
                            )}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card> 
    )
}

export default AgentSettings